import { useState, useEffect, useMemo, useCallback } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { flagEmoji } from "@/data/bracketData";
import { TOURNAMENTS, loadTournamentData, type TournamentData } from "@/data/tournaments";
import { motion } from "framer-motion";
import {
  Trophy, Users, Medal, Crown, ArrowRight,
  Plus, Lock, Clock, AlertCircle, CheckCircle2, Activity,
  TrendingUp, Gift, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShareScreen } from "@/components/ShareScreen";
import { PrizePoolBanner } from "@/components/PrizePoolBanner";
import { TournamentLogo } from "@/components/TournamentLogo";
import { toast } from "sonner";

interface BracketSummary {
  id: string;
  name: string;
  champion: string | null;
  score: number;
  is_locked: boolean;
  tournament_id: string | null;
  picks: any;
  updated_at?: string;
}

interface GroupSummary {
  id: string;
  name: string;
  member_count: number;
  is_public: boolean;
}

interface LeaderEntry {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  score: number;
  champion: string | null;
  tournament_id: string | null;
}

interface TournamentLeaderboard {
  tournament_id: string;
  name: string;
  shortName: string;
  logoUrl?: string;
  emoji: string;
  rows: LeaderEntry[];
  yourRank: number | null;
}

const WORLD_CUP_ID = "world-cup-2026";

function tournamentRoute(t: { id: string; slug?: string } | null | undefined, kind: "bracket" | "leaderboard") {
  const isWC = !t || t.id === WORLD_CUP_ID;
  if (isWC) return kind === "bracket" ? "/bracket" : "/leaderboard";
  return `/tournaments/${t!.slug || t!.id}/${kind}`;
}

function totalMatchCount(tournamentId: string | null): number {
  const cfg = TOURNAMENTS.find(t => t.id === (tournamentId || WORLD_CUP_ID));
  if (!cfg) return 0;
  return cfg.rounds.reduce((sum, r) => sum + r.matchCount, 0);
}

function picksMade(picks: any): number {
  const mp = picks?.matchPicks || {};
  return Object.values(mp).filter(Boolean).length;
}

function daysUntil(iso: string): number {
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.ceil((target - now) / 86_400_000);
}

type SliceState<T> = { status: "loading" | "ok" | "error"; data: T; error?: string };

// Per-query soft timeout. Individual queries are allowed to fail
// without taking the whole page down — the section shows its own
// retry instead.
const withTimeout = <T,>(p: PromiseLike<T>, ms: number, label: string): Promise<T> =>
  Promise.race([
    Promise.resolve(p),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);

export function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  // Per-section state so one slow/flaky query can never black out the page.
  const [bracketsSlice, setBracketsSlice] = useState<SliceState<BracketSummary[]>>({ status: "loading", data: [] });
  const [leaderboardsSlice, setLeaderboardsSlice] = useState<SliceState<TournamentLeaderboard[]>>({ status: "loading", data: [] });
  const [groupsSlice, setGroupsSlice] = useState<SliceState<GroupSummary[]>>({ status: "loading", data: [] });
  const [tournamentDataMap, setTournamentDataMap] = useState<Record<string, TournamentData | null>>({});
  const [referralData, setReferralData] = useState<{ referral_code: string; referral_count: number } | null>(null);
  const [showShareScreen, setShowShareScreen] = useState(false);

  const brackets = bracketsSlice.data;
  const tournamentLeaderboards = leaderboardsSlice.data;
  const groups = groupsSlice.data;

  const loadLeaderboards = useCallback(async (tournamentIds: string[]) => {
    if (!user) return;
    if (tournamentIds.length === 0) {
      setLeaderboardsSlice({ status: "ok", data: [] });
      return;
    }
    try {
      const rankRowsRes: any = await withTimeout(
        supabase
          .from("brackets")
          .select("user_id, score, champion, tournament_id")
          .in("tournament_id", tournamentIds)
          .order("score", { ascending: false }),
        10000,
        "rank-rows"
      );
      if (rankRowsRes.error) throw rankRowsRes.error;

      const allRankedUserIds = Array.from(new Set((rankRowsRes.data as any[] || []).map(r => r.user_id)));
      const profileMap: Record<string, any> = {};
      if (allRankedUserIds.length) {
        try {
          const profilesRes: any = await withTimeout(
            supabase.from("profiles").select("id, display_name, avatar_url").in("id", allRankedUserIds),
            10000,
            "profiles"
          );
          (profilesRes.data || []).forEach((p: any) => { profileMap[p.id] = p; });
        } catch (e) {
          console.warn("[Dashboard] profiles fetch failed, using fallbacks:", e);
        }
      }

      const rankRowsByTournament: Record<string, any[]> = {};
      ((rankRowsRes.data as any[]) || []).forEach(row => {
        const tid = row.tournament_id || WORLD_CUP_ID;
        (rankRowsByTournament[tid] ||= []).push(row);
      });

      const leaderboards: TournamentLeaderboard[] = tournamentIds.map(tid => {
        const cfg = TOURNAMENTS.find(t => t.id === tid);
        const rows = rankRowsByTournament[tid] || [];
        const ranked = rows.map((r, i) => ({
          user_id: r.user_id,
          display_name: profileMap[r.user_id]?.display_name || "Player",
          avatar_url: profileMap[r.user_id]?.avatar_url || null,
          score: r.score,
          champion: r.champion,
          tournament_id: r.tournament_id,
          rank: i + 1,
        }));
        const yourRow = ranked.find(r => r.user_id === user.id);
        return {
          tournament_id: tid,
          name: cfg?.name || tid,
          shortName: cfg?.shortName || tid,
          logoUrl: cfg?.logoUrl,
          emoji: cfg?.emoji || "🏆",
          rows: ranked.slice(0, 3),
          yourRank: yourRow?.rank ?? null,
        };
      });
      setLeaderboardsSlice({ status: "ok", data: leaderboards });
    } catch (err: any) {
      console.error("[Dashboard] leaderboards fetch failed:", err);
      let hadData = false;
      setLeaderboardsSlice(prev => {
        hadData = prev.data.length > 0;
        return {
          status: "error",
          data: prev.data,
          error: "Couldn't load leaderboards. Please retry.",
        };
      });
      if (hadData) {
        toast.error("Couldn't refresh leaderboards", {
          description: "Showing the last version. Retry in a moment.",
        });
      }
    }
  }, [user]);

  const loadGroups = useCallback(async (groupIds: string[]) => {
    if (groupIds.length === 0) {
      setGroupsSlice({ status: "ok", data: [] });
      return;
    }
    try {
      const [groupRowsRes, groupMemberCountsRes]: any = await Promise.all([
        withTimeout(
          supabase.from("bracket_groups").select("id, name, is_public").in("id", groupIds),
          10000,
          "groups"
        ),
        withTimeout(
          supabase.from("bracket_group_members").select("group_id").in("group_id", groupIds),
          10000,
          "group-member-counts"
        ),
      ]);
      if (groupRowsRes.error) throw groupRowsRes.error;

      const countsByGroup: Record<string, number> = {};
      ((groupMemberCountsRes.data as any[]) || []).forEach((m: any) => {
        countsByGroup[m.group_id] = (countsByGroup[m.group_id] || 0) + 1;
      });
      const groupRows = (groupRowsRes.data as any[]) || [];
      setGroupsSlice({
        status: "ok",
        data: groupRows.map((g: any) => ({ ...g, member_count: countsByGroup[g.id] || 0 })),
      });
    } catch (err: any) {
      console.error("[Dashboard] groups fetch failed:", err);
      setGroupsSlice({
        status: "error",
        data: [],
        error: "Couldn't load your groups. Please retry.",
      });
    }
  }, []);

  // ── Section loader: brackets + derives groupIds/tournamentIds,
  //    then kicks leaderboards + groups in parallel. Each sub-section
  //    owns its own error/loading state; one failure never propagates. ──
  const loadDashboard = useCallback(async () => {
    if (!user) return;

    // Preserve previous data through the loading state. Any transient
    // failure keeps the last-known-good rows on screen until a fresh
    // success replaces them.
    setBracketsSlice(prev => ({ status: "loading", data: prev.data }));
    setLeaderboardsSlice(prev => ({ status: "loading", data: prev.data }));
    setGroupsSlice(prev => ({ status: "loading", data: prev.data }));

    // Referral is optional and independent — kick it off fire-and-forget.
    if (user.email) {
      withTimeout(
        supabase.rpc("lookup_referral", { p_email: user.email.toLowerCase() }).single(),
        8000,
        "referral"
      )
        .then((r: any) => {
          if (r?.data?.referral_code) {
            setReferralData({ referral_code: r.data.referral_code, referral_count: r.data.referral_count });
          }
        })
        .catch((e) => console.warn("[Dashboard] referral lookup failed:", e));
    }

    // ── Brackets + group membership: run independently so one slow query
    //    doesn't tank the other. Brackets gates the leaderboard fetch (needs
    //    tournamentIds); group-members gates the groups fetch (needs groupIds).
    //    User-facing error copy is scrubbed of internal labels.

    // Brackets
    let userBrackets: BracketSummary[] = [];
    try {
      const bracketsRes: any = await withTimeout(
        supabase
          .from("brackets")
          .select("id, name, champion, score, is_locked, tournament_id, picks, updated_at")
          .eq("user_id", user.id),
        10000,
        "brackets"
      );
      if (bracketsRes.error) throw bracketsRes.error;
      userBrackets = (bracketsRes.data as BracketSummary[]) || [];
      setBracketsSlice({ status: "ok", data: userBrackets });
    } catch (err: any) {
      console.error("[Dashboard] brackets fetch failed:", err);
      let priorData: BracketSummary[] = [];
      setBracketsSlice(prev => {
        priorData = prev.data;
        return {
          status: "error",
          data: prev.data,
          error: "Couldn't load your brackets. Check your connection and try again.",
        };
      });
      // When we had data already, don't change the UI — just flash a toast.
      // The full error card only shows for cold-start failures.
      if (priorData.length > 0) {
        toast.error("Couldn't refresh brackets", {
          description: "Showing the last version we loaded. Try again in a moment.",
        });
        userBrackets = priorData;
      }
    }

    const tournamentIds = Array.from(new Set(userBrackets.map(b => b.tournament_id || WORLD_CUP_ID)));

    // Group membership (drives the Groups section only) — runs independently
    (async () => {
      try {
        const groupMembersRes: any = await withTimeout(
          supabase.from("bracket_group_members").select("group_id").eq("user_id", user.id),
          10000,
          "group-members"
        );
        if (groupMembersRes.error) throw groupMembersRes.error;
        const groupIds = ((groupMembersRes.data as { group_id: string }[] | null) || []).map(m => m.group_id);
        void loadGroups(groupIds);
      } catch (err: any) {
        console.error("[Dashboard] group-members fetch failed:", err);
        let hadData = false;
        setGroupsSlice(prev => {
          hadData = prev.data.length > 0;
          return {
            status: "error",
            data: prev.data,
            error: "Couldn't load your groups. Please retry.",
          };
        });
        if (hadData) {
          toast.error("Couldn't refresh groups", {
            description: "Showing the last version. Retry in a moment.",
          });
        }
      }
    })();

    // Leaderboards (depends on tournamentIds from brackets)
    void loadLeaderboards(tournamentIds);

    // ── Non-critical: tournament team data (for champion logos) ──
    if (tournamentIds.length) {
      Promise.allSettled(
        tournamentIds.map(async (tid) => [tid, await loadTournamentData(tid)] as const)
      ).then(results => {
        const dataMap: Record<string, TournamentData | null> = {};
        results.forEach(r => {
          if (r.status === "fulfilled") {
            const [tid, data] = r.value;
            dataMap[tid] = data;
          } else {
            console.warn("[Dashboard] tournament data load failed:", r.reason);
          }
        });
        setTournamentDataMap(dataMap);
      });
    }
  }, [user, loadLeaderboards, loadGroups]);

  // Effect must come AFTER loadDashboard's const declaration — referencing
  // it in the dep array before initialization throws a TDZ ReferenceError
  // at render, which is minified to "Cannot access 'O' before initialization"
  // and crashed the app for every signed-in user post-magic-link.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Auth finished but no user — show empty states (public-dashboard mode).
      setBracketsSlice({ status: "ok", data: [] });
      setLeaderboardsSlice({ status: "ok", data: [] });
      setGroupsSlice({ status: "ok", data: [] });
      return;
    }
    loadDashboard();
  }, [user, authLoading, loadDashboard]);

  // ── Derived: champion logo helper ──
  const getChampionVisual = (bracket: BracketSummary) => {
    if (!bracket.champion) return null;
    const tid = bracket.tournament_id || WORLD_CUP_ID;
    const data = tournamentDataMap[tid];
    if (!data) return { type: "code" as const, value: bracket.champion };
    const teamLogo = data.teamLogos?.[bracket.champion];
    if (teamLogo) return { type: "img" as const, value: teamLogo };
    const flag = data.flagEmoji?.[bracket.champion] || flagEmoji[bracket.champion];
    if (flag) return { type: "emoji" as const, value: flag };
    return { type: "code" as const, value: bracket.champion };
  };

  // ── Derived: next action ──
  const nextAction = useMemo(() => {
    if (!brackets.length) {
      return {
        kind: "empty" as const,
        title: "Pick your first bracket",
        body: "Browse 9 active tournaments and start a bracket in under a minute.",
        cta: { label: "Browse Tournaments", to: "/tournaments" },
      };
    }
    const unlocked = brackets.filter(b => !b.is_locked);
    if (unlocked.length) {
      const ranked = unlocked
        .map(b => {
          const cfg = TOURNAMENTS.find(t => t.id === (b.tournament_id || WORLD_CUP_ID));
          const total = totalMatchCount(b.tournament_id);
          const made = picksMade(b.picks);
          const days = cfg?.lockDeadline ? daysUntil(cfg.lockDeadline) : 999;
          return { b, cfg, total, made, days };
        })
        .sort((a, b) => a.days - b.days);
      const top = ranked[0];
      const tName = top.cfg?.shortName || "Tournament";
      const remaining = top.total - top.made;
      const deadlineText = top.days > 0 && top.days < 60
        ? `Deadline in ${top.days} day${top.days !== 1 ? "s" : ""}`
        : top.days <= 0 ? "Picks lock soon" : "Open for picks";
      return {
        kind: "lock" as const,
        title: top.made === 0 ? `Start your ${tName} bracket` : `Finish your ${tName} bracket`,
        body: `${top.made}/${top.total} picks made · ${deadlineText}${remaining > 0 ? ` · ${remaining} to go` : ""}`,
        cta: { label: top.made === 0 ? "Start Picking" : "Continue", to: tournamentRoute(top.cfg, "bracket") },
      };
    }
    // All locked — celebrate
    const totalScore = brackets.reduce((s, b) => s + b.score, 0);
    return {
      kind: "locked" as const,
      title: "All brackets locked in",
      body: `${brackets.length} bracket${brackets.length !== 1 ? "s" : ""} · ${totalScore} pts so far. Watch live standings during matches.`,
      cta: { label: "View Leaderboard", to: "/leaderboard" },
    };
  }, [brackets]);

  // ── Derived: activity feed ──
  const activityItems = useMemo(() => {
    const items: { id: string; icon: any; title: string; meta?: string; tone: "info" | "warn" | "success" }[] = [];

    brackets.forEach(b => {
      const cfg = TOURNAMENTS.find(t => t.id === (b.tournament_id || WORLD_CUP_ID));
      const tName = cfg?.shortName || "Tournament";
      if (!b.is_locked) {
        const made = picksMade(b.picks);
        const total = totalMatchCount(b.tournament_id);
        items.push({
          id: `unlocked-${b.id}`,
          icon: AlertCircle,
          title: made === 0 ? `${tName}: bracket not started` : `${tName}: ${total - made} picks remaining`,
          meta: cfg?.lockDeadline ? `Locks ${new Date(cfg.lockDeadline).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : undefined,
          tone: "warn",
        });
      } else {
        items.push({
          id: `locked-${b.id}`,
          icon: CheckCircle2,
          title: `${tName}: bracket locked`,
          meta: `${b.score} pts so far`,
          tone: "success",
        });
      }
    });

    groups.forEach(g => {
      items.push({
        id: `group-${g.id}`,
        icon: Users,
        title: `Member of ${g.name}`,
        meta: `${g.member_count} member${g.member_count !== 1 ? "s" : ""}`,
        tone: "info",
      });
    });

    return items.slice(0, 6);
  }, [brackets, groups]);

  // Initial skeleton only while the critical brackets query is in flight
  // AND we've never successfully loaded anything. After first success,
  // subsequent reloads keep the old data on screen so it never flashes
  // back to a full skeleton.
  const bracketsLoading = bracketsSlice.status === "loading";
  const leaderboardsLoading = leaderboardsSlice.status === "loading";
  const groupsLoading = groupsSlice.status === "loading";

  const totalScore = brackets.reduce((s, b) => s + b.score, 0);
  const bestRank = tournamentLeaderboards
    .map(tl => tl.yourRank)
    .filter((r): r is number => r !== null)
    .sort((a, b) => a - b)[0];

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-4 h-4 text-rank-gold" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-rank-silver" />;
    if (rank === 3) return <Medal className="w-4 h-4 text-rank-bronze" />;
    return <span className="text-xs font-display text-muted-foreground">{rank}</span>;
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="space-y-6">
      {/* ── Always-on prize-pool CTA for free users ── */}
      <PrizePoolBanner />

      {/* ── Top: Next Action + 2 mini KPIs ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className={`md:col-span-2 p-5 rounded-2xl border ${
            nextAction.kind === "lock"
              ? "bg-gradient-to-br from-primary/15 via-primary/5 to-transparent border-primary/30"
              : "bg-card border-border"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
              nextAction.kind === "lock" ? "bg-primary/20"
              : nextAction.kind === "locked" ? "bg-primary/15"
              : "bg-secondary"
            }`}>
              {nextAction.kind === "lock" ? <Clock className="w-5 h-5 text-primary" />
                : nextAction.kind === "locked" ? <CheckCircle2 className="w-5 h-5 text-primary" />
                : <Sparkles className="w-5 h-5 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-1">Next up</p>
              <h2 className="font-display text-xl font-bold text-foreground leading-tight">{nextAction.title}</h2>
              <p className="text-sm text-muted-foreground mt-1">{nextAction.body}</p>
              <Link to={nextAction.cta.to}>
                <Button size="sm" className="mt-3 font-display gap-1.5">
                  {nextAction.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 gap-3">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="p-4 rounded-2xl bg-card border border-border">
            <div className="w-9 h-9 rounded-lg bg-gold/15 flex items-center justify-center mb-2">
              <TrendingUp className="w-5 h-5 text-gold" />
            </div>
            <p className="text-2xl font-display font-bold text-foreground">{bestRank ? `#${bestRank}` : "—"}</p>
            <p className="text-xs text-muted-foreground">Best Rank</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="p-4 rounded-2xl bg-card border border-border">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center mb-2">
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <p className="text-2xl font-display font-bold text-foreground">{totalScore}</p>
            <p className="text-xs text-muted-foreground">Total Pts</p>
          </motion.div>
        </div>
      </div>

      {/* ── Two-column main ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Your Brackets grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="rounded-2xl bg-card border border-border overflow-hidden"
        >
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              <h3 className="font-display text-lg font-bold text-foreground">Your Brackets</h3>
            </div>
            <Link to="/my-brackets">
              <Button variant="ghost" size="sm" className="text-xs gap-1">All <ArrowRight className="w-3 h-3" /></Button>
            </Link>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {bracketsLoading && brackets.length === 0 ? (
              <>
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}
              </>
            ) : bracketsSlice.status === "error" && brackets.length === 0 ? (
              <div className="sm:col-span-2 flex flex-col items-center justify-center py-10 gap-3 text-center">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                </div>
                <p className="text-xs text-muted-foreground max-w-[260px]">{bracketsSlice.error}</p>
                <Button variant="outline" size="sm" onClick={loadDashboard} className="font-display">Retry</Button>
              </div>
            ) : null}
            {brackets.map(b => {
              const cfg = TOURNAMENTS.find(t => t.id === (b.tournament_id || WORLD_CUP_ID));
              const tourneyName = cfg?.shortName || b.tournament_id || "Tournament";
              const total = totalMatchCount(b.tournament_id);
              const made = picksMade(b.picks);
              const champ = getChampionVisual(b);
              const champTeamName = b.champion && tournamentDataMap[b.tournament_id || WORLD_CUP_ID]?.teams?.find(t => t.code === b.champion)?.name;

              return (
                <Link
                  key={b.id}
                  to={tournamentRoute(cfg, "bracket")}
                  className="group rounded-xl border border-border bg-background/50 p-4 hover:border-primary/40 hover:bg-card transition-all flex flex-col"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <TournamentLogo logoUrl={cfg?.logoUrl} emoji={cfg?.emoji} alt={tourneyName} size="w-12 h-12" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-sm font-bold text-foreground truncate">{tourneyName}</p>
                      <div className="mt-1">
                        {b.is_locked
                          ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-primary/20 via-primary/5 to-transparent text-primary text-[10px] font-semibold"><Lock className="w-2.5 h-2.5" /> Locked</span>
                          : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] font-semibold"><Clock className="w-2.5 h-2.5" /> In progress</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 space-y-2">
                    {/* Champion */}
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-secondary/50 flex items-center justify-center overflow-hidden shrink-0">
                        {champ?.type === "img"
                          ? <img src={champ.value} alt="" className="w-6 h-6 object-contain" />
                          : champ?.type === "emoji"
                            ? <span className="text-base leading-none">{champ.value}</span>
                            : <Trophy className="w-3.5 h-3.5 text-muted-foreground/50" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Champion</p>
                        <p className="text-xs font-semibold text-foreground truncate">{champTeamName || b.champion || "Not picked"}</p>
                      </div>
                    </div>

                    {/* Pick progress */}
                    {total > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Picks</p>
                          <p className={`text-[11px] font-semibold ${made === total ? "text-primary" : "text-foreground"}`}>
                            {made}/{total}{made === total ? " ✓" : ""}
                          </p>
                        </div>
                        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              made === total
                                ? "bg-gradient-to-r from-primary via-primary to-primary/80 shadow-[0_0_8px_rgba(208,255,0,0.5)]"
                                : "bg-gradient-to-r from-primary/80 to-primary/50"
                            }`}
                            style={{ width: `${(made / total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                    <div>
                      <p className="font-display text-xl font-bold text-primary leading-none">{b.score}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">points</p>
                    </div>
                    <div className="text-xs font-semibold text-primary group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                      {b.is_locked ? "View" : "Continue"} <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* + New bracket tile */}
            <Link
              to="/tournaments"
              className="rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 transition-all flex flex-col items-center justify-center gap-2 p-6 min-h-[180px] text-center group"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-display text-sm font-bold text-foreground">New bracket</p>
                <p className="text-xs text-muted-foreground mt-0.5">9 tournaments to choose from</p>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* RIGHT: Per-tournament Leaderboards + Groups + Activity */}
        <div className="space-y-5">
          {/* Per-tournament leaderboards */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="rounded-2xl bg-card border border-border overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-primary" />
                <h3 className="font-display text-lg font-bold text-foreground">Leaderboards</h3>
              </div>
              <Link to="/leaderboard">
                <Button variant="ghost" size="sm" className="text-xs gap-1">Full <ArrowRight className="w-3 h-3" /></Button>
              </Link>
            </div>
            <div className="divide-y divide-border">
              {leaderboardsLoading && tournamentLeaderboards.length === 0 ? (
                <div className="p-3 space-y-3">
                  {[1, 2].map(i => <Skeleton key={i} className="h-16 rounded-lg" />)}
                </div>
              ) : leaderboardsSlice.status === "error" && tournamentLeaderboards.length === 0 ? (
                <div className="py-8 text-center px-4 space-y-2">
                  <AlertCircle className="w-6 h-6 mx-auto text-destructive/70" />
                  <p className="text-xs text-muted-foreground">{leaderboardsSlice.error}</p>
                  <Button variant="outline" size="sm" onClick={loadDashboard} className="font-display text-xs">Retry</Button>
                </div>
              ) : tournamentLeaderboards.length === 0 ? (
                <div className="py-8 text-center px-4">
                  <Medal className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
                  <p className="text-sm text-muted-foreground">Enter a bracket to see standings</p>
                </div>
              ) : (
                tournamentLeaderboards.map(tl => (
                  <div key={tl.tournament_id} className="p-3">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-md bg-secondary/40 flex items-center justify-center overflow-hidden shrink-0">
                          {tl.logoUrl
                            ? <img src={tl.logoUrl} alt="" className="w-5 h-5 object-contain" />
                            : <span className="text-xs">{tl.emoji}</span>}
                        </div>
                        <p className="font-display text-xs font-bold text-foreground truncate">{tl.shortName}</p>
                      </div>
                      {tl.yourRank && (
                        <span className="text-[10px] font-semibold text-primary px-1.5 py-0.5 rounded bg-primary/10">
                          You: #{tl.yourRank}
                        </span>
                      )}
                    </div>
                    {tl.rows.length === 0 ? (
                      <p className="text-xs text-muted-foreground px-2 py-1.5">No rankings yet</p>
                    ) : (
                      <div className="space-y-0.5">
                        {tl.rows.map((entry, i) => {
                          const isYou = entry.user_id === user?.id;
                          return (
                            <div key={entry.user_id}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${isYou ? "bg-primary/5" : ""}`}>
                              <div className="w-5 flex items-center justify-center">{getRankIcon(i + 1)}</div>
                              <div className="w-6 h-6 rounded-full bg-secondary border border-border flex items-center justify-center text-xs">
                                {entry.avatar_url || entry.display_name.charAt(0).toUpperCase()}
                              </div>
                              <p className={`text-xs flex-1 truncate ${isYou ? "text-primary font-semibold" : "text-foreground"}`}>
                                {entry.display_name}{isYou && " (you)"}
                              </p>
                              <span className="font-display text-xs text-foreground">{entry.score}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </motion.div>

          {/* Your Groups */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="rounded-2xl bg-card border border-border overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <h3 className="font-display text-lg font-bold text-foreground">Your Groups</h3>
              </div>
              <Link to="/groups">
                <Button variant="ghost" size="sm" className="text-xs gap-1">All <ArrowRight className="w-3 h-3" /></Button>
              </Link>
            </div>
            <div className="p-3 space-y-2">
              {groupsLoading && groups.length === 0 ? (
                <>
                  {[1, 2].map(i => <Skeleton key={i} className="h-14 rounded-xl" />)}
                </>
              ) : groupsSlice.status === "error" && groups.length === 0 ? (
                <div className="py-6 text-center space-y-2">
                  <AlertCircle className="w-6 h-6 mx-auto text-destructive/70" />
                  <p className="text-xs text-muted-foreground">{groupsSlice.error}</p>
                  <Button variant="outline" size="sm" onClick={loadDashboard} className="font-display text-xs">Retry</Button>
                </div>
              ) : groups.length === 0 ? (
                <div className="py-6 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
                  <p className="text-sm text-muted-foreground mb-2">No groups yet</p>
                  <Link to="/groups">
                    <Button size="sm" variant="outline" className="font-display">Join or Create</Button>
                  </Link>
                </div>
              ) : (
                groups.map(g => (
                  <Link key={g.id} to={`/groups/${g.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-secondary/50 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{g.name}</p>
                      <p className="text-xs text-muted-foreground">{g.member_count} member{g.member_count !== 1 ? "s" : ""}</p>
                    </div>
                    <Badge variant={g.is_public ? "secondary" : "outline"} className="text-[10px]">
                      {g.is_public ? "Public" : "Private"}
                    </Badge>
                  </Link>
                ))
              )}
            </div>
          </motion.div>

          {/* Activity feed */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="rounded-2xl bg-card border border-border overflow-hidden"
          >
            <div className="flex items-center gap-2 p-4 border-b border-border">
              <Activity className="w-5 h-5 text-primary" />
              <h3 className="font-display text-lg font-bold text-foreground">Activity</h3>
            </div>
            <div className="p-2">
              {activityItems.length === 0 ? (
                <div className="py-6 text-center px-3">
                  <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
                  <p className="text-sm text-muted-foreground">Nothing here yet</p>
                </div>
              ) : (
                activityItems.map(item => (
                  <div key={item.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-secondary/30 transition-colors">
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                      item.tone === "warn" ? "bg-amber-500/15 text-amber-500"
                      : item.tone === "success" ? "bg-gradient-to-r from-primary/20 via-primary/5 to-transparent text-primary"
                      : "bg-primary/10 text-primary"
                    }`}>
                      <item.icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground leading-tight">{item.title}</p>
                      {item.meta && <p className="text-[11px] text-muted-foreground mt-0.5">{item.meta}</p>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>

          {/* Referral card (kept — useful) */}
          {referralData && (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              className="rounded-2xl bg-card border border-border overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-lg font-bold text-foreground">Referrals</h3>
                </div>
                <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => setShowShareScreen(true)}>
                  Share <ArrowRight className="w-3 h-3" />
                </Button>
              </div>
              <div className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="font-display text-xl text-primary">{referralData.referral_count}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {referralData.referral_count === 0 ? "No referrals yet" : `${referralData.referral_count} friend${referralData.referral_count !== 1 ? "s" : ""} joined`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {referralData.referral_count < 1 ? "Share to earn a free bracket" : referralData.referral_count < 3 ? "2 more for Early Access" : referralData.referral_count < 5 ? `${5 - referralData.referral_count} more for VIP` : "VIP Status unlocked! 🏆"}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {showShareScreen && referralData && (
        <ShareScreen referralCode={referralData.referral_code} onClose={() => setShowShareScreen(false)} />
      )}
    </motion.div>
  );
}
